/**
 * Round Events — unified per-round timeline for Hide and Seek.
 *
 * Two event kinds live in the same timeline today:
 *   - `marco_polo`     — mirrors an existing Marco Polo activation.
 *   - `ruleset_change` — a scheduled full snapshot of hider + seeker rules
 *                        that becomes effective at a specific offset from
 *                        the round's seeking-start.
 *
 * Rules for scheduling:
 *   - Scheduled `ruleset_change` events must be at least 2 minutes apart on
 *     the offset axis. On-the-fly admin changes (`ROUND_EVENT_APPLY_NOW`)
 *     bypass this buffer and are logged as retroactive events.
 *   - Lead time (5–60s) is per-event and drives the pre-fire warning
 *     broadcast. Warning is UX only; rules officially change at the
 *     server-authoritative `effectiveAt`.
 *
 * Timestamp handling for DQ evaluation reuses FPR's
 * `combatLogToUtcTimestamp` — see `apps/api-server/src/utils/timezone.ts`.
 */
import { normalizeHnsRoleRules } from './hnsRules';
export const RoundEventKind = {
    MARCO_POLO: 'marco_polo',
    RULESET_CHANGE: 'ruleset_change',
};
/** Minimum offset gap between two scheduled `ruleset_change` events, in seconds. */
export const ROUND_EVENT_MIN_GAP_SECONDS = 120;
/** Allowed lead time range for `ruleset_change` events, in seconds. */
export const ROUND_EVENT_LEAD_TIME_MIN_SECONDS = 5;
export const ROUND_EVENT_LEAD_TIME_MAX_SECONDS = 60;
// ============================================================================
// Type guards
// ============================================================================
export function isRulesetChangePayload(payload) {
    return (typeof payload === 'object' &&
        payload !== null &&
        'hiderRules' in payload &&
        'seekerRules' in payload);
}
/**
 * Read a stored ruleset change's payload, each role's rules in the current
 * shape (normalizeHnsRoleRules: rules saved as old booleans, or missing, read
 * as before).
 */
export function normalizeRulesetChangePayload(payload) {
    const stored = payload && typeof payload === 'object' ? payload : {};
    return {
        hiderRules: normalizeHnsRoleRules(stored.hiderRules),
        seekerRules: normalizeHnsRoleRules(stored.seekerRules),
    };
}
export function isMarcoPoloEventPayload(payload) {
    return !isRulesetChangePayload(payload);
}
